import { useCallback, useSyncExternalStore } from 'react';

/**
 * Observa uma media query.
 *
 * Usado para escolher entre tabela (desktop) e cartões (mobile) renderizando
 * **apenas uma** das duas árvores. Manter as duas no DOM e alternar por CSS
 * dobraria os nós em uma lista de 100 linhas e criaria nomes acessíveis
 * duplicados para a mesma ação.
 *
 * `useSyncExternalStore` é a forma canônica de ler estado externo no React:
 * nada de `setState` dentro de efeito, e o valor já sai correto no primeiro
 * render.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const list = window.matchMedia?.(query);
      if (!list) return () => {};

      list.addEventListener('change', onStoreChange);
      return () => list.removeEventListener('change', onStoreChange);
    },
    [query],
  );

  const getSnapshot = useCallback(() => window.matchMedia?.(query).matches ?? false, [query]);

  // Sem DOM (SSR/teste sem stub), assume o caso mais simples: não corresponde.
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/** Breakpoint `md` do Tailwind — mesmo valor usado nas classes responsivas. */
export const DESKTOP_QUERY = '(min-width: 48rem)';
